/**
 * Der **Befundvorrat**: die zweite Hauptansicht neben dem Revisionsstrom.
 *
 * Sie beantwortet nicht „was ist heute passiert", sondern **„was ist offen"** —
 * eine Zeile je **Stelle**, quer über alle Revisionen, vorbelegt auf `offen`,
 * sortiert nach Schwere und dann nach Übereinstimmung.
 *
 * Vier Festlegungen tragen sie:
 *
 * - **Die Zeile ist eine Stelle, kein Einzelbefund.** Drei Agenten an derselben
 *   Stelle ergeben eine Zeile mit „3 von 3" und nicht drei Zeilen — sonst stünde
 *   ausgerechnet der sicherste Befund als Dreifachrauschen im Vorrat.
 * - **Zahl, Schwere und Übereinstimmung stehen nebeneinander**, nicht
 *   verrechnet. Solange die Schweregrade der Agenten ungemessen sind, wäre jede
 *   Gewichtung erfunden.
 * - **Was fehlt, meldet die Ansicht selbst.** Liegen im gefilterten Ausschnitt
 *   Revisionen ohne vollständige Begutachtung, steht das über der Liste, mit
 *   einem Weg in den entsprechend gefilterten Strom. Ohne ihn wäre eine leere
 *   Liste doppeldeutig: „nichts gefunden" oder „nie angesehen".
 * - **Der Revisionsstrom bleibt gleichwertig daneben stehen.** Er ist nicht die
 *   Rückfallebene: Eine Revision *ohne* Befund kommt hier gar nicht vor, und nur
 *   er zeigt Bearbeitungsstand und Review-Empfehlung.
 * - **Die Anzeigeschwelle je Projekt ist eine Vorbelegung** (Spec v1.3, Story
 *   241): Sie greift beim Öffnen und sonst nirgends. Eine Änderung am Filter
 *   macht alles wieder sichtbar, und solange etwas ausgeblendet ist, sagt die
 *   Ansicht das mit der Zahl. Erhoben und gespeichert wird weiter alles — wer
 *   hier Befunde wegwürfe, nähme sich die spätere Kalibrierung.
 * - **Von jeder Zeile führt die Übergabe weiter** (Spec v1.3, Story 222): ein
 *   Knopf, der den kopierfertigen Text holt, ihn in die Zwischenablage legt und
 *   ihn zur Vorschau zeigt. Erzeugt wird er im Server; die Ansicht stellt keinen
 *   Text zusammen, den sie nur zur Hälfte hat.
 * - **Diese Liste setzt nichts** (ADR-0022). Sie sagt in der Spalte „Bewertung",
 *   wie es um eine Stelle steht, und lässt bewerten, wo der Code steht: in der
 *   Detailansicht, in die der Klick auf die Zeile führt. „Fehlalarm" aus einer
 *   Tabellenzeile heraus wäre ein Klick ins Blaue — sie trägt Pfad, Zeilen und
 *   Befundtext, aber nicht den Code, an dem sich entscheidet, ob die Maschine
 *   recht hatte. Die **Übergabe** bleibt trotzdem, wo sie ist: Sie setzt nichts,
 *   sondern holt etwas.
 *
 * Gefiltert wird im Server. Die Oberfläche hält keine zweite, eigene Liste, aus
 * der sie herausrechnet — sonst stimmte die Seitenzahl nicht mehr mit dem
 * überein, was zu sehen ist.
 *
 * **Die Bauform: Liste mit vielen Filtern und Blättern**, dieselbe wie der
 * Revisionsstrom (#41, nachgezogen mit #136). Die **Werkzeugleiste** trägt links
 * die eine Handlung auf den ganzen Ausschnitt — das Zurücksetzen — und rechts
 * den Seitennavigator, seither einmal statt zweimal. Die Filterachsen bleiben im
 * Inhalt: Sie wählen aus, *was* in der Liste steht, und ein Band von 49 Punkten
 * trägt sie nicht. Ausgeschrieben steht die Regel in
 * `docs/human/2026-08-27-werkzeugleiste-und-sektionen.md`.
 */

import { useEffect, useRef, useState } from "react";
import type { ReactElement } from "react";

import {
  Button,
  Checkbox,
  Chip,
  DataTable,
  DatePicker,
  Icon,
  Select,
  StatusPill,
  TextField,
} from "../../design-system";
import type { DataTableColumn } from "../../design-system";
import type {
  DeliveryCut,
  FindingKind,
  FindingOrigin,
  FindingSites,
  FindingSitesApi,
  FindingStatus,
  ProjectOption,
  Qualification,
  Severity,
  Site,
  SiteAuthor,
  SiteQuery,
} from "../../api/client";
import { formatNumber } from "../../formatting";
import { SITE_START, type SiteFilter } from "../../navigation/place";
import { Toolbar } from "../../shell/Toolbar";
import { NumericCell } from "../cells";
import { DeliveryMark } from "../delivery";
import { VisitedMarker } from "../visited";
import { FindingCell } from "../findingCell";
import { IssueReferenceCell } from "../issueReferenceCell";
import {
  FINDINGS_WIDTH,
  ISSUE_REFERENCE_WIDTH,
  MESSAGE_MIN_SITES,
  PERSON_WIDTH,
  PROJECT_WIDTH,
  REVISION_WIDTH,
} from "../listWidths";
import { MessageCell } from "../messageCell";
import { PersonCell } from "../personCell";
import {
  GONE,
  labelOfJudgement,
  QUALIFICATION_LABELS,
  QUALIFICATION_TONES,
  SEVERITY_TONES,
  STATUS_LABELS,
  STATUS_TONES,
} from "../findings";
import {
  HANDOFF_TITLE,
  HandoffButton,
  HandoffDialog,
  SITE_HANDOFF_HINT,
  useHandover,
} from "../handoff";
import { FilterReset, ListPage, ListSection } from "../list";
import { Notice, describe, type Loaded } from "../loading";

/** Eine Bildschirmhöhe voll Stellen. Der Vorrat kommt nie im Ganzen. */
const PER_PAGE = 25;

const ALL_PROJECTS = "Alle Projekte";
const ANY_SEVERITY = "Schwere: alle";
const ANY_KIND = "Art: alle";
const ANY_STATUS = "Status: alle";
const ANY_QUALIFICATION = "Bewertung: alle";
const ANY_ORIGIN = "Urheber: alle";

const NOTHING = "Unter diesen Filtern ist keine Stelle offen.";

/** Die Reihenfolge, in der die Schweregrade stehen: von schwer nach leicht. */
const SEVERITIES: Severity[] = ["schwer", "mittel", "hinweis"];

/** Die fünf Befundarten des Glossars, in der Reihenfolge, in der es sie führt. */
const KINDS: FindingKind[] = ["syntax", "konvention", "nicht-produktiv", "struktur", "migration"];

/**
 * Die Reihenfolge des **Lebenszyklus** im Filter: erst der offene Befund, dann
 * die drei Ausgänge, auf denen er den Vorrat verlässt.
 */
const STATUSES: FindingStatus[] = ["open", "fixed", "dismissed", "superseded"];

/**
 * Die **Qualifizierung** steht als eigener Filter daneben (Spec v1.4, Story
 * 252) und ist auf „alle" vorbelegt: Sie sagt, ob die Maschine recht hatte, und
 * nicht, ob hier noch Arbeit steht. Über eine Liste gemischt wäre „bestätigt und
 * offen" — der bestbelegte Arbeitsauftrag im System — nicht zu bestellen.
 */
const QUALIFICATIONS: Qualification[] = ["confirmed", "acknowledged", "rejected"];

/**
 * Die Urheber, deutsch beschriftet. Die Werte bleiben englisch — sie sind die
 * geschlossene Menge der API (ADR-0009).
 */
const ORIGIN_LABELS: Record<FindingOrigin, string> = {
  agent: "Agent",
  pattern: "Kontrolle",
  person: "Person",
};

const ORIGINS: FindingOrigin[] = ["agent", "pattern", "person"];

/**
 * Der Knopf **„an meinem Code"** (ADR-0028; Glossar, „Befunde an meinem Code").
 *
 * Ein eigener Knopf und kein Eintrag in einem Select wie im Revisionsstrom: Hier
 * gibt es keinen Kennungsfilter, den man erweitern könnte.
 */
const OWN_CODE = "an meinem Code";

/**
 * Dasselbe für den, dem keine SVN-Kennung zugeordnet ist: **abgeschaltet und mit
 * dem Grund im Klartext.**
 *
 * Nicht eine leere Liste, die sich wie ein sauberer Stand liest — und nicht bloß
 * ein Titel, den man erst beim Draufzeigen findet.
 */
const OWN_CODE_WITHOUT_IDENTITY = `${OWN_CODE} — keine SVN-Kennung zugeordnet`;

/** Was der Knopf nimmt — und was er ausdrücklich nicht nimmt. */
const OWN_CODE_TITLE =
  "Die Befunde, deren Revision Sie selbst übergeben haben — über alle Ihnen zugeordneten "
  + "SVN-Kennungen. Bestandsbefunde tragen keinen Committer und erscheinen dabei nicht. "
  + "Nicht dasselbe wie Ihre eigenen Befunde: Das sind die von Ihnen verfassten.";

/** Warum der Knopf abgeschaltet dasteht — und wer daran etwas ändern kann. */
const OWN_CODE_UNAVAILABLE =
  "Ihnen ist keine SVN-Kennung zugeordnet; welche Revisionen Ihre sind, ist damit nicht zu "
  + "beantworten. Ein Administrator ordnet die Kennung in der Verwaltung zu.";

/** Was die **Anzeigeschwelle** ist — und was sie ausdrücklich nicht ist. */
const THRESHOLD_TITLE =
  "Der Schweregrad je Projekt, unterhalb dessen eine Stelle in der Vorbelegung nicht erscheint. "
  + "Erhoben und gespeichert wird trotzdem alles; abgewählt steht der Vorrat vollständig da.";

/** Warum eine Zeile ohne Sprung dasteht. */
const NO_REVISION =
  "Zu dieser Stelle gibt es keine Revision, die ihre Zeilen zeigt — sie stammt aus einem " +
  "Bestandslauf und steht im Bestand.";

/**
 * Die gesetzten Filter ohne die Seite. `null` heißt bei jedem: danach wird nicht
 * gefiltert.
 *
 * Sie stehen zusammen mit der Seite als `SiteFilter` in der Adresse — dort liegt
 * auch die Vorbelegung (`navigation/place`, `SITE_START`): `offen` und die
 * Anzeigeschwelle, und was davon abweicht, ist der Adresse anzusehen.
 */
type Chosen = Omit<SiteFilter, "page">;

/** Der Ausschnitt ohne die Seite — die Seite führt die Ansicht getrennt. */
function chosenOf(filter: SiteFilter): Chosen {
  const { page: _page, ...chosen } = filter;

  return chosen;
}

/**
 * Die Vorbelegung dieser Leiste — **nicht das Nichts**: `offen` und die
 * Anzeigeschwelle stehen darin (`SITE_START`). Gegen sie wird verglichen, und auf
 * sie stellt „Filter zurücksetzen" zurück; das ist zugleich die Adresse `/sites`
 * ohne Abfrageteil.
 */
const START_CHOSEN: Chosen = chosenOf(SITE_START);

/**
 * **Die Spalten des Vorrats** — als ausgeführte Konstante und nicht im JSX,
 * damit die Zahlen, aus denen die Breite entsteht, prüfbar sind
 * (`listWidths.test.ts`). Dieselbe Entscheidung wie am Revisionsstrom
 * (`STREAM_COLUMNS`), und aus demselben Grund: `scripts/verify` sieht keine Pixel.
 *
 * Die vier Spalten, die es in beiden Listen gibt — Projekt, Rev., Ticket,
 * Befunde —, tragen dieselbe Zahl aus `listWidths.ts`. Ungedeckelt ist auch hier
 * genau eine: der Kommentar.
 */
export const SITE_COLUMNS: DataTableColumn[] = [
  { key: "here", label: "", width: 24 },
  // Achtzig Pixel: mehr brauchen weder der Kopf „Schwere" noch das breiteste
  // Abzeichen darunter („Hinweis").
  { key: "severity", label: "Schwere", width: 80 },
  { key: "project", label: "Projekt", maxWidth: PROJECT_WIDTH, width: PROJECT_WIDTH },
  // Der **Entwickler**, der die Stelle übergeben hat — unmittelbar hinter dem
  // Projekt, wo er im Revisionsstrom schon steht (#129): **wo** die Stelle
  // herkommt, **wer** sie übergeben hat, **woran** dort gearbeitet wurde. Wer eine
  // Zeile liest, sah bisher Datei, Revision, Kommentar und Befund — aber nicht
  // den Menschen dahinter.
  //
  // Die Kopfzeile heißt „Entwickler", der Schlüssel bleibt `person` (ADR-0009);
  // die Zelle ist dieselbe wie im Strom (`personCell.tsx`). Eine Stelle ohne
  // Revision zeigt hier **nichts**: Bestandsbefunde tragen keinen Committer.
  {
    key: "person",
    label: "Entwickler",
    maxWidth: PERSON_WIDTH,
    width: PERSON_WIDTH,
  },
  // Sie war einmal die Restspalte und ist es nicht mehr: Der Rest gehört der
  // Kommentarvorschau, in beiden Listen derselben Spalte. Ein Pfad gewinnt an
  // Breite ohnehin kaum — er endet im Titel vollständig und wird von hinten
  // gelesen. Deckel und Breite sind deshalb dieselbe Zahl.
  { key: "file", label: "Datei", maxWidth: 180, width: 180 },
  // `Rev.` mit `hint` wie im Strom, und die Schreibweise `r86.351` bleibt.
  // Rechtsbündig, und die Zahl darunter steht in der Zahlenzelle
  // (`views/cells.tsx`): In beiden Listen dieselbe Spalte.
  {
    key: "revision",
    label: "Rev.",
    hint: "Revision",
    align: "right",
    width: REVISION_WIDTH,
  },
  { key: "lines", label: "Zeilen", align: "right", width: 70 },
  // Der **Commit-Kommentar** der Revision, in die der Sprung führt — und die eine
  // ungedeckelte Spalte dieser Liste. Vor dem Ticketverweis, der aus ihm stammt:
  // dieselbe Reihenfolge wie im Strom.
  //
  // Damit fällt eine Festlegung aus #113, und sie fällt ausdrücklich: Der
  // Kommentar sollte im Vorrat keine Spalte werden. Der Grund für die Umkehr
  // steht im Betrieb — wer im Vorrat eine Stelle liest, entscheidet, ob er sie
  // anfasst, und die Absicht des Commits ist dafür dieselbe Auskunft wie im
  // Strom. Der Verweis allein trägt sie nur, wenn es einen Vorgang gibt.
  {
    key: "message",
    label: "Kommentar",
    width: MESSAGE_MIN_SITES,
  },
  // Der **Ticketverweis** aus dem Commit-Kommentar (#113) — dieselbe Zelle wie im
  // Revisionsstrom. Datei, Revision und Zeilen sagen, **wo** es ist, Kommentar
  // und Verweis sagen, **woran** dort gearbeitet wurde, und die Befundzelle,
  // was gefunden wurde.
  //
  // **Mit Deckel** (#129): Das Kürzel ist kurz, wo die Ticketliste eines trägt
  // (`DEV-435`, #131), und die lange Form `owner/repo#nummer`, wo sie keines hat.
  // Ohne `maxWidth` setzt `DataTable` an der
  // Zelle kein `overflow: hidden`, die Spalte wächst über ihre Zuweisung und zieht
  // die Liste hinter sich her — genau die Regel aus #120 an der Spalte, die sie
  // ausgelassen hatte.
  {
    key: "issues",
    label: "Ticket",
    maxWidth: ISSUE_REFERENCE_WIDTH,
    width: ISSUE_REFERENCE_WIDTH,
  },
  // Zahl, Titel und Urheber in **einer** Spalte: Sie beantworten dieselbe
  // Frage — was hier gefunden wurde und von wem.
  {
    key: "findings",
    label: "Befunde",
    width: FINDINGS_WIDTH,
  },
  // **Eine Spalte für beide Achsen und den Urheber**, und keine Knöpfe daneben:
  // Die Spalte „Behebung" ist mit ADR-0022 gefallen.
  //
  // **Hundertfünfundsechzig Pixel und nicht mehr die 230 der gefallenen Spalte**
  // (#129). Die 230 waren geerbt und nie gemessen. Was tatsächlich darin steht:
  // zwei Abzeichen, die umbrechen, und darunter die Unterschrift des Urteils, die
  // ebenfalls umbricht. Die harte Untergrenze ist deshalb das **breiteste
  // einzelne Abzeichen**, denn eine Pille bricht in sich nicht um:
  // „zutreffend, unerheblich", dreiundzwanzig Zeichen.
  //
  // Gerechnet gegen die eine Zahl, die in #120 am Bildschirm gemessen wurde: 184
  // Pixel für „auffällig, aber kein Befund", siebenundzwanzig Zeichen in
  // derselben Schrift (`--fs-11` halbfett), mit derselben Polsterung an Abzeichen
  // und Zelle. Vier Zeichen weniger sind rund zweiundzwanzig Pixel weniger, also
  // 162 — 165 lässt drei Pixel Luft. Wer nachmisst und mehr misst, setzt das
  // Gemessene ein.
  {
    key: "verdict",
    label: "Bewertung",
    maxWidth: 165,
    width: 165,
  },
  // Hundertfünfzehn Pixel: der Knopf mit seinem Zeichen, und darunter die
  // Zustellmarke, die mit dem Kurzformat des Abstands schmaler geworden ist.
  { key: "handoff", label: HANDOFF_TITLE, width: 115 },
];

/**
 * Die **Kennung einer Zeile**: Sie sagt, welche Zeile zuletzt geöffnet war, und
 * überlebt dafür das Neuholen der Liste. Aus dem Anker gebildet und nicht aus
 * dem Listenplatz — der ist nach einem Filterwechsel ein anderer.
 */
export function siteKey(site: Site): string {
  return `${site.projectId}:${site.path}@${site.revisionNumber}:${site.startLine}-${site.endLine}`;
}

/**
 * `onOpenSite` führt an die Stelle — in die Revisionsansicht, mit markiertem
 * Zeilenbereich. `onShowIncomplete` führt in die Revisionsliste, gefiltert auf
 * die unvollständig begutachteten Revisionen desselben Projekts.
 *
 * Der Vorrat kennt beide Ansichten nicht; er reicht nach oben, was er hat, und
 * die Hülle entscheidet, was sie zeigt.
 *
 * `filter` ist der Ausschnitt, mit dem die Liste aufgebaut wird — aus der
 * Adresse oder aus dem Chronikeintrag, in den Zurück geführt hat; `onFilter`
 * meldet jede Änderung nach oben, damit sie dorthin zurückfindet. Die Liste
 * bleibt die Besitzerin ihrer Filter; die Adresse ist ihr Gedächtnis, nicht ihr
 * Vormund. `marked` ist die zuletzt geöffnete Zeile.
 *
 * `hasSvnIdentity` sagt, ob der fragenden Person eine SVN-Kennung zugeordnet ist
 * (ADR-0028). Ohne Zuordnung steht der Knopf „an meinem Code" abgeschaltet da und
 * nennt den Grund; vorbelegt ist **nein**, denn wer die Auskunft noch nicht hat,
 * verspricht nichts, was er nicht halten kann.
 */
export function FindingSitesView({
  client,
  onOpenSite,
  onShowIncomplete,
  filter = SITE_START,
  onFilter,
  marked = null,
  hasSvnIdentity = false,
}: {
  client: FindingSitesApi;
  onOpenSite: (site: Site) => void;
  onShowIncomplete: (projectId: number | null) => void;
  filter?: SiteFilter;
  onFilter?: (filter: SiteFilter) => void;
  marked?: string | null;
  hasSvnIdentity?: boolean;
}): ReactElement {
  const [chosen, setChosen] = useState<Chosen>(() => chosenOf(filter));
  const [page, setPage] = useState(filter.page);
  const query: SiteQuery = { page, perPage: PER_PAGE, ...chosen };
  const state = useAnswer(client, query);
  const handover = useHandover();

  // Ein geänderter Filter beginnt wieder auf der ersten Seite: Seite drei eines
  // anderen Filters ist eine andere Seite drei.
  const change = (next: Partial<Chosen>): void => {
    const merged = { ...chosen, ...next };

    setChosen(merged);
    setPage(1);
    onFilter?.({ ...merged, page: 1 });
  };

  const turn = (next: number): void => {
    setPage(next);
    onFilter?.({ ...chosen, page: next });
  };

  const listed = state.kind === "loaded" ? state.value : null;
  const pages = listed?.pages ?? 1;
  const total = listed?.total ?? 0;

  return (
    <>
      {/*
        **Die Werkzeugleiste dieser Ansicht** (#41, #136). Links steht die eine
        Handlung auf den ganzen Ausschnitt — das Zurücksetzen —, rechts das
        Blättern. Die Filterachsen bleiben im Inhalt: Sie wählen aus, *was* in
        der Liste steht, und ein Band von 49 Punkten trägt sie nicht.
      */}
      <Toolbar
        paging={
          total === 0
            ? null
            : { page, pages, rangeLabel: rangeOf(page, PER_PAGE, total), onTurn: turn }
        }
      >
        <FilterReset chosen={chosen} start={START_CHOSEN} onReset={() => change(START_CHOSEN)} />
      </Toolbar>

      <ListPage>
        <ListSection
          title="Befunde"
          glyph="list-checks"
          first
          headerRight={listed === null ? null : <Chip>{`${formatNumber(total)} Stellen`}</Chip>}
          filters={
            <>
              <FilterBar
                projects={listed?.projects ?? []}
                chosen={chosen}
                change={change}
                hasSvnIdentity={hasSvnIdentity}
              />

              {listed === null || listed.incompleteRevisions === 0 ? null : (
                <IncompleteNotice
                  revisions={listed.incompleteRevisions}
                  onShow={() => onShowIncomplete(chosen.projectId)}
                />
              )}

              {listed === null || listed.belowThreshold === 0 ? null : (
                <ThresholdNotice
                  sites={listed.belowThreshold}
                  onShow={() => change({ thresholds: false })}
                />
              )}
            </>
          }
        >
          <div style={{ padding: "var(--sp-2) var(--sp-5)" }}>
            {state.kind === "loading" ? <Notice text="Wird geladen ..." glyph="timer" /> : null}
            {state.kind === "failed" ? (
              <Notice text={state.message} glyph="unplug" color="var(--ac-red)" />
            ) : null}
            {listed !== null && listed.sites.length === 0 ? <Notice text={NOTHING} /> : null}
          </div>

          {listed !== null && listed.sites.length > 0 ? (
            <DataTable
              /*
                Die Zeile trägt seit #142 keine Kennung mehr, sondern nur ihre
                Zellen. Der Platz in der Liste sagt, welche Stelle gemeint ist —
                dieselbe Auskunft wie das `at`, das die Zeile vorher mitführte.
              */
              onRowClick={(_row, index) => {
                const site = listed.sites[index];

                if (site !== undefined && site.revisionId !== null) {
                  onOpenSite(site);
                }
              }}
              columns={SITE_COLUMNS}
              rows={listed.sites.map((site) => ({
                cells: {
                  here: <VisitedMarker shown={marked === siteKey(site)} />,
                  project: site.projectName,
                  revision: <NumericCell>{`r${formatNumber(site.revisionNumber)}`}</NumericCell>,
                  lines: <NumericCell>{linesOf(site)}</NumericCell>,
                  severity: <SeverityCell site={site} />,
                  file: <FileCell site={site} />,
                  person: <PersonCell svnIdentity={site.svnIdentity} personName={site.personName} />,
                  message: <MessageCell text={site.message} />,
                  issues: <IssueReferenceCell references={site.issueReferences} />,
                  findings: <FindingsCell site={site} />,
                  verdict: <VerdictCell site={site} />,
                  handoff: (
                    <span
                      style={{
                        display: "inline-flex",
                        flexDirection: "column",
                        alignItems: "flex-start",
                        gap: "var(--sp-3)",
                      }}
                    >
                      <HandoffButton
                        onHandOver={() =>
                          handover.begin(SITE_HANDOFF_HINT, () => client.handoff(site), cutOf(site))
                        }
                      />
                      {/* Die **Zustellung** steht unter dem Knopf, aus dem sie
                          hervorgeht — und leise: Sie sagt „darüber wurde geredet"
                          und nicht „erledigt" (ADR-0027, ADR-0012). */}
                      <DeliveryMark at={site.deliveredAt} />
                    </span>
                  ),
                },
              }))}
            />
          ) : null}
        </ListSection>

        {handover.shown === null ? null : (
          <HandoffDialog
            shown={handover.shown}
            client={client}
            onCopy={handover.copyAgain}
            onClose={handover.close}
          />
        )}
      </ListPage>
    </>
  );
}

/**
 * Der **Zuschnitt**, den diese Zeile zustellen darf (#102).
 *
 * `null`, wo es keine Revision gibt: Eine Stelle aus einem Bestandslauf hat
 * keinen Commit-Kommentar und damit keinen Ticketverweis — und ohne Revision
 * gibt es auch keine Abgabe, an der die Zustellung hinge.
 */
function cutOf(site: Site): DeliveryCut | null {
  return site.revisionId === null
    ? null
    : {
        revisionId: site.revisionId,
        anchor: {
          projectId: site.projectId,
          path: site.path,
          revisionNumber: site.revisionNumber,
          startLine: site.startLine,
          endLine: site.endLine,
        },
      };
}

/**
 * Der Hinweis, mit dem der Vorrat sagt, was ihm fehlt (Spec v1.3, Story 221).
 *
 * Er steht **über** der Liste und nicht unter ihr: Er ist der Vorbehalt gegen
 * alles, was darunter steht, und ein Vorbehalt hinter der Aussage kommt zu spät.
 * Der Weg führt in den Revisionsstrom, denn nur dort steht der Bearbeitungsstand
 * — im Vorrat kommt eine Revision ohne Befund gar nicht vor.
 */
function IncompleteNotice({
  revisions,
  onShow,
}: {
  revisions: number;
  onShow: () => void;
}): ReactElement {
  return (
    <div
      role="status"
      aria-label="Fehlende Begutachtung"
      style={{
        display: "flex",
        alignItems: "center",
        flexWrap: "wrap",
        gap: "var(--sp-5)",
        padding: "var(--sp-5) var(--sp-6)",
        borderBottom: "1px solid var(--line-f)",
        background: "var(--bg-sunken)",
        color: "var(--ac-amber)",
        fontSize: "var(--fs-12)",
      }}
    >
      <Icon name="triangle-alert" size={15} />
      <span style={{ textWrap: "pretty" }}>
        {revisions === 1
          ? "1 Revision unvollständig begutachtet"
          : `${formatNumber(revisions)} Revisionen unvollständig begutachtet`}
        {" — was dort läge, steht hier nicht."}
      </span>
      <Button variant="outline" size="sm" onClick={onShow}>
        Bei den Revisionen ansehen
      </Button>
    </div>
  );
}

/**
 * Der Hinweis, mit dem der Vorrat sagt, dass die **Anzeigeschwelle** gerade
 * etwas wegnimmt (Spec v1.3, Story 241).
 *
 * Er ist **kein Alarm**, sondern eine Auskunft: Die Schwelle hat jemand
 * eingestellt, und sie tut genau das, wofür sie da ist. Ohne ihn wäre die
 * gekürzte Liste von einer vollständigen nicht zu unterscheiden — und eine
 * Schwelle, von der niemand erfährt, wäre ein Löschwerkzeug mit freundlichem
 * Namen.
 *
 * **Nichts ist verloren.** Der Knopf daneben nimmt die Schwelle für diesen Blick
 * zurück; erhoben und gespeichert ist ohnehin alles.
 */
function ThresholdNotice({
  sites,
  onShow,
}: {
  sites: number;
  onShow: () => void;
}): ReactElement {
  return (
    <div
      role="status"
      aria-label="Anzeigeschwelle"
      style={{
        display: "flex",
        alignItems: "center",
        flexWrap: "wrap",
        gap: "var(--sp-5)",
        padding: "var(--sp-5) var(--sp-6)",
        borderBottom: "1px solid var(--line-f)",
        background: "var(--bg-sunken)",
        color: "var(--fg-6)",
        fontSize: "var(--fs-12)",
      }}
    >
      <Icon name="eye-off" size={15} />
      <span style={{ textWrap: "pretty" }}>
        {sites === 1
          ? "1 Stelle unterhalb der Anzeigeschwelle ausgeblendet"
          : `${formatNumber(sites)} Stellen unterhalb der Anzeigeschwelle ausgeblendet`}
        {" — erhoben und gespeichert ist sie trotzdem."}
      </span>
      <Button variant="outline" size="sm" onClick={onShow}>
        Trotzdem anzeigen
      </Button>
    </div>
  );
}

/**
 * Die Filterleiste: Projekt, Pfad, Art, Schwere, Status, Urheber, Zeitraum, der
 * eigene Code und die Anzeigeschwelle.
 *
 * Die Projekte kommen aus derselben Antwort wie die Zeilen und bleiben stehen,
 * auch wenn gerade gefiltert wird — sonst nähme ein gesetzter Filter die Wege
 * zurück, ihn zu wechseln.
 */
function FilterBar({
  projects,
  chosen,
  change,
  hasSvnIdentity,
}: {
  projects: ProjectOption[];
  chosen: Chosen;
  change: (next: Partial<Chosen>) => void;
  hasSvnIdentity: boolean;
}): ReactElement {
  const project = projects.find((one) => one.id === chosen.projectId);

  return (
    <div
      role="group"
      aria-label="Filter"
      style={{
        display: "flex",
        alignItems: "center",
        flexWrap: "wrap",
        gap: "var(--sp-4)",
        padding: "var(--sp-5) var(--sp-6)",
        borderBottom: "1px solid var(--line-f)",
      }}
    >
      {/*
        Die Breiten der Filterfelder bleiben die gemessenen und wandern nur vom
        eigenen Prop des abgelösten Auswahlfelds in dessen `style`: Das V3-Feld
        führt kein `width`. Ein Token gibt es dafür in keiner der beiden
        Generationen — die Zahl ist je Filter am längsten Eintrag abgelesen, und
        ein Token je Filter wäre keines. Zur Bemessung siehe #140.
      */}
      <Select
        style={{ width: 190 }}
        height={28}
        value={project?.name ?? ALL_PROJECTS}
        options={[ALL_PROJECTS, ...projects.map((one) => one.name)]}
        onSelect={(picked) =>
          change({ projectId: projects.find((one) => one.name === picked)?.id ?? null })
        }
      />
      <PathFilter path={chosen.path} onPath={(path) => change({ path })} />
      <Select
        style={{ width: 170 }}
        height={28}
        value={chosen.severity ?? ANY_SEVERITY}
        options={[ANY_SEVERITY, ...SEVERITIES]}
        onSelect={(picked) => change({ severity: SEVERITIES.find((one) => one === picked) ?? null })}
      />
      <Select
        style={{ width: 180 }}
        height={28}
        value={chosen.kind ?? ANY_KIND}
        options={[ANY_KIND, ...KINDS]}
        onSelect={(picked) => change({ kind: KINDS.find((one) => one === picked) ?? null })}
      />
      <Select
        style={{ width: 210 }}
        height={28}
        value={chosen.status === null ? ANY_STATUS : STATUS_LABELS[chosen.status]}
        options={[ANY_STATUS, ...STATUSES.map((one) => STATUS_LABELS[one])]}
        onSelect={(picked) =>
          change({ status: STATUSES.find((one) => STATUS_LABELS[one] === picked) ?? null })
        }
      />
      <Select
        style={{ width: 210 }}
        height={28}
        value={
          chosen.qualification === null
            ? ANY_QUALIFICATION
            : QUALIFICATION_LABELS[chosen.qualification]
        }
        options={[ANY_QUALIFICATION, ...QUALIFICATIONS.map((one) => QUALIFICATION_LABELS[one])]}
        onSelect={(picked) =>
          change({
            qualification:
              QUALIFICATIONS.find((one) => QUALIFICATION_LABELS[one] === picked) ?? null,
          })
        }
      />
      <Select
        style={{ width: 170 }}
        height={28}
        value={chosen.origin === null ? ANY_ORIGIN : ORIGIN_LABELS[chosen.origin]}
        options={[ANY_ORIGIN, ...ORIGINS.map((one) => ORIGIN_LABELS[one])]}
        onSelect={(picked) =>
          change({ origin: ORIGINS.find((one) => ORIGIN_LABELS[one] === picked) ?? null })
        }
      />
      <DayFilter label="Von" day={chosen.from} onDay={(from) => change({ from })} />
      <DayFilter label="Bis" day={chosen.to} onDay={(to) => change({ to })} />
      {/*
        Vor der Anzeigeschwelle: Er sagt, **welche** Stellen gemeint sind, und die
        Schwelle sagt, welche davon anzuzeigen sind — die engere Auswahl steht vor
        der Anzeigeregel.
      */}
      {/*
        Die beiden Hinweise hängen am Aufrufort und nicht am Baustein: Die
        Checkbox des Skills nimmt ihre Beschriftung als Text und legt das
        `<input>` selbst in ein `<label>` — ein Element darin, das einen Titel
        tragen könnte, gibt es nicht mehr. Beide Sätze sind zu lang für die
        Beschriftung und zu genau, um zu entfallen.
      */}
      <span title={hasSvnIdentity ? OWN_CODE_TITLE : OWN_CODE_UNAVAILABLE}>
        <Checkbox
          checked={chosen.committer === "me"}
          disabled={!hasSvnIdentity}
          label={hasSvnIdentity ? OWN_CODE : OWN_CODE_WITHOUT_IDENTITY}
          onChange={(own) => change({ committer: own ? "me" : null })}
        />
      </span>
      <span title={THRESHOLD_TITLE}>
        <Checkbox
          checked={chosen.thresholds}
          label="Anzeigeschwelle"
          onChange={(thresholds) => change({ thresholds })}
        />
      </span>
    </div>
  );
}

/**
 * Der Pfadfilter: ein Ausschnitt, kein ganzer Pfad.
 *
 * Er greift beim Abschicken und nicht bei jedem Anschlag — sonst liefe je
 * getipptem Buchstaben eine Anfrage, und der Vorrat flackerte durch alle
 * Zwischenstände eines Wortes.
 *
 * **Wird der Filter von außen gesetzt, zieht das Feld nach.** Der Entwurf gehört
 * dem Feld, der Filter der Liste; setzt „Filter zurücksetzen" den Filter zurück,
 * stünde sonst weiter ein Pfad im Feld, nach dem die Liste nicht mehr filtert —
 * die Leiste behauptete das Gegenteil der Liste. Beim Abschicken ist es kein
 * Sprung: Dort trägt das Feld denselben Text, den es hergegeben hat.
 */
function PathFilter({
  path,
  onPath,
}: {
  path: string | null;
  onPath: (path: string | null) => void;
}): ReactElement {
  const [draft, setDraft] = useState(path ?? "");

  useEffect(() => {
    setDraft(path ?? "");
  }, [path]);

  const commit = (): void => {
    const trimmed = draft.trim();

    onPath(trimmed === "" ? null : trimmed);
  };

  return (
    <span
      onBlur={commit}
      onKeyDown={(event) => {
        if (event.key === "Enter") {
          commit();
        }
      }}
      style={{ width: 220 }}
    >
      {/*
        Die Feldaktion des V3-Felds sitzt rechts am Feld und ist ein Knopf, nicht
        bloß eine Glyphe. Sie tut deshalb, was auch die Eingabetaste tut: den
        Entwurf übernehmen. Die Höhe kommt aus dem Knopfmaß der Leisten — das Feld
        steht hier neben Auswahlfeldern mit 28 px und nicht in einer Maske.
      */}
      <TextField
        style={{ height: "var(--h-button)", width: "100%" }}
        value={draft}
        placeholder="Pfad enthält ..."
        action="scan-search"
        actionTip="Pfad übernehmen"
        onAction={commit}
        onChange={setDraft}
      />
    </span>
  );
}

/**
 * Ein Tag des Zeitraums.
 *
 * Der Kalender des Design-Systems schreibt deutsch — `TT.MM.JJJJ` —, die API
 * nimmt `JJJJ-MM-TT` (ADR-0009). Die Umrechnung steht hier und nicht im Kopf des
 * Anwenders.
 */
function DayFilter({
  label,
  day,
  onDay,
}: {
  label: string;
  day: string | null;
  onDay: (day: string | null) => void;
}): ReactElement {
  return (
    <span
      role="group"
      aria-label={label}
      style={{ display: "inline-flex", alignItems: "center", gap: "var(--sp-4)", width: 190 }}
    >
      <span style={{ flex: "none", fontSize: "var(--fs-11)", color: "var(--fg-6)" }}>
        {label}
      </span>
      <span style={{ flex: 1 }}>
        <DatePicker height={28} value={asGerman(day)} onSelect={(picked) => onDay(asDay(picked))} />
      </span>
    </span>
  );
}

/**
 * **Wo** es ist: der Pfad im Projekt.
 *
 * Ohne den überwachten Unterbaum, weil das Projekt seine eigene Spalte hat: Sein
 * Pfad stünde sonst in jeder Zeile noch einmal und schöbe dabei genau das aus
 * dem Bild, was die Zeilen unterscheidet. Der ganze Pfad ist damit nicht weg —
 * er steht im Titel, und er ist es auch, der als Anker gilt (ADR-0006).
 */
function FileCell({ site }: { site: Site }): ReactElement {
  return (
    <span
      title={site.path}
      style={{
        display: "block",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        fontSize: "var(--fs-12)",
        color: "var(--fg-1)",
      }}
    >
      {site.relativePath}
    </span>
  );
}

/**
 * Die Schwere: **ausschließlich die höchste**, als Schild.
 *
 * Die niedrigste stand hier einmal als „bis …" daneben, wo die Befunde einer
 * Stelle auseinandergingen. Sie machte die Spalte in manchen Zeilen zweizeilig
 * und in anderen nicht, und sie beantwortete eine Frage, die in der Spalte gar
 * nicht gestellt ist: Sortiert, gefiltert und gelesen wird nach der höchsten.
 * Wer die einzelnen Urteile sehen will, öffnet die Stelle.
 */
function SeverityCell({ site }: { site: Site }): ReactElement {
  // „hinweis" trägt keine Farbe und steht deshalb als `Chip` da (#141): Der
  // niedrigste Grad ist die Abwesenheit einer Warnung und kein Zustand.
  const tone = SEVERITY_TONES[site.severity];

  return tone === null ? (
    <Chip>{site.severity}</Chip>
  ) : (
    <StatusPill tone={tone}>{site.severity}</StatusPill>
  );
}

/**
 * Was hier gefunden wurde: die Zahl, der Titel des relevantesten Befundes und
 * die Bezeichner aller Urheber.
 *
 * **Die Zahl ist der Indikator und nicht die Übereinstimmung.** „n von m
 * Agenten" stand hier einmal in einer eigenen Spalte; sie beantwortete dieselbe
 * Frage zweimal — wie viele hier etwas gesehen haben — und kostete dafür die
 * Breite, an der die Liste waagerecht scrollte.
 *
 * **Ein Kontrollmuster steht als „Regel" und nicht unter seiner Beschriftung.**
 * Es ist keine Meinung, sondern eine Regel, die immer anschlägt (ADR-0010); in
 * einer Reihe von Agentennamen läse sich „Zuweisung in einer Bedingung" wie ein
 * weiterer Agent. Welche Regel es war, steht im Titel.
 *
 * Die Zahl trägt im Titel die Befundarten dieser Stelle — sie hatten eine eigene
 * Spalte, und die Frage „welche Art" stellt sich erst, wenn eine Zeile schon
 * ausgewählt ist.
 *
 * Der Aufbau selbst steht in `FindingCell`: Der Revisionsstrom zeigt denselben
 * Fund, und zwei Aufbauten wären zwei Antworten auf dieselbe Frage.
 */
function FindingsCell({ site }: { site: Site }): ReactElement {
  return (
    <FindingCell
      total={site.total}
      severity={site.severity}
      title={site.title}
      authors={site.authors}
      countTitle={kindsOf(site)}
    />
  );
}

/**
 * Die Befundarten dieser Stelle — **alle**, im Titel der Zahl.
 *
 * Zwei Agenten dürfen dieselbe Stelle verschieden einordnen; welche Einordnung
 * „gilt", entscheidet diese Ansicht nicht, und sie zeigt deshalb beide. Im Titel
 * und nicht in einer Spalte: Die Frage „welche Art" stellt sich, wenn eine Zeile
 * schon ausgewählt ist.
 */
function kindsOf(site: Site): string | undefined {
  return site.kinds.length === 0 ? undefined : `Art: ${site.kinds.join(" · ")}`;
}

/**
 * Die **Bewertung** einer Stelle, wie die Liste sie liest: der **Lebenszyklus**
 * und die **Qualifizierung** nebeneinander, ohne dass die eine die andere
 * verdeckt (Spec v1.4, Story 253) — darunter die Unterschrift unter dem Urteil
 * (Story 257).
 *
 * **Sie zeigt und setzt nicht** (ADR-0022). An dieser Stelle standen einmal die
 * Knöpfe der Behebung; gesetzt wird ab jetzt in der Detailansicht, wo der Code
 * steht. Was hier steht, ist die ganze Auskunft über den Stand einer Stelle —
 * und ein Klick auf die Zeile führt dorthin, wo sich daran etwas ändern lässt.
 *
 * Je eine Menge und kein Wert: Eine Stelle fasst mehrere Befunde zusammen, und
 * die können verschieden stehen. Unter der Vorbelegung `offen` steht genau ein
 * Zeichen des Lebenszyklus da; ohne Urteil steht daneben nichts.
 *
 * **Wer bewertet hat, steht ausgeschrieben da und nicht nur im Titel.** Das
 * Urteil gilt für alle, und eines, das man erst beim Draufzeigen findet, kann
 * beim Lesen der Liste niemand hinterfragen (ADR-0022). Es steht in derselben
 * gedämpften Nebenzeile wie das Namensband der Befundzelle daneben.
 */
function VerdictCell({ site }: { site: Site }): ReactElement {
  const judged = labelOfJudgement(site.qualifiedBy, site.qualifiedAt);

  return (
    <span
      style={{
        display: "inline-flex",
        flexDirection: "column",
        alignItems: "flex-start",
        gap: "var(--sp-3)",
      }}
    >
      <span style={{ display: "inline-flex", flexWrap: "wrap", gap: "var(--sp-3)" }}>
        {site.statuses.map((status) => {
          const tone = STATUS_TONES[status];

          return tone === null ? (
            <Chip key={status}>{STATUS_LABELS[status]}</Chip>
          ) : (
            <StatusPill key={status} tone={tone}>
              {STATUS_LABELS[status]}
            </StatusPill>
          );
        })}
        {site.qualifications.map((qualification) => {
          const tone = QUALIFICATION_TONES[qualification];

          return tone === null ? (
            <Chip key={qualification}>{QUALIFICATION_LABELS[qualification]}</Chip>
          ) : (
            <StatusPill key={qualification} tone={tone}>
              {QUALIFICATION_LABELS[qualification]}
            </StatusPill>
          );
        })}
        {site.revisionId === null ? (
          <span style={{ color: "var(--fg-6)" }} title={NO_REVISION}>
            —
          </span>
        ) : null}
      </span>

      {judged === null ? null : (
        <span
          style={{
            fontSize: "var(--fs-11)",
            color: "var(--fg-6)",
            textWrap: "pretty",
          }}
        >
          {judged}
        </span>
      )}
    </span>
  );
}

/**
 * Fragt beim Erscheinen und bei jeder geänderten Frage neu — und **nicht im
 * Takt**.
 *
 * Der Revisionsstrom fragt fortlaufend nach, weil in ihm etwas eingeht; der
 * Vorrat wird abgearbeitet, und eine Liste, die sich unter der Hand umsortiert,
 * während jemand die dritte Zeile liest, ist schlechter als eine, die stehen
 * bleibt. Er ist auch der Weg, auf dem der Revisionszugang die Auslieferung
 * fremder Befunde vermerkt (ADR-0013) — ein Takt vermerkte fortlaufend, was
 * niemand gelesen hat.
 */
function useAnswer(client: FindingSitesApi, query: SiteQuery): Loaded<FindingSites> {
  const [state, setState] = useState<Loaded<FindingSites>>({ kind: "loading" });
  const latest = useRef(query);
  latest.current = query;

  // Die Frage steht als Zeichenkette in der Abhängigkeit: Das Objekt entsteht
  // bei jedem Rendern neu, und ein neues Objekt löste jedes Mal eine neue
  // Anfrage aus.
  const asked = JSON.stringify(query);

  useEffect(() => {
    let discarded = false;

    client.findingSites(latest.current).then(
      (value) => {
        if (!discarded) {
          setState({ kind: "loaded", value });
        }
      },
      (error: unknown) => {
        if (!discarded) {
          setState({ kind: "failed", message: describe(error) });
        }
      },
    );

    return () => {
      discarded = true;
    };
  }, [client, asked]);

  return state;
}

/** Der Zeilenbereich einer Stelle. Eine einzelne Zeile steht ohne Bindestrich da. */
function linesOf(site: Site): string {
  return site.startLine === site.endLine
    ? formatNumber(site.startLine)
    : `${formatNumber(site.startLine)}–${formatNumber(site.endLine)}`;
}

/** `2026-08-09` als `09.08.2026` — die Schreibweise des Kalenders. */
function asGerman(day: string | null): string {
  if (day === null) {
    return "";
  }

  const parts = day.split("-");

  return parts.length === 3 ? `${parts[2]}.${parts[1]}.${parts[0]}` : "";
}

/** `09.08.2026` als `2026-08-09` — die Schreibweise der API. Leer heißt: kein Filter. */
function asDay(german: string): string | null {
  const parts = german.split(".");

  return parts.length === 3 ? `${parts[2]}-${parts[1]}-${parts[0]}` : null;
}

/** „1–25 von 57" — die Schreibweise des Design-Systems für einen Seitenbereich. */
function rangeOf(page: number, perPage: number, total: number): string {
  const first = (page - 1) * perPage + 1;

  return `${formatNumber(first)}–${formatNumber(Math.min(page * perPage, total))} von ${formatNumber(total)}`;
}
